import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import { organizationUsersService } from "../services/organization-users.service";
import type {
	CreateNewOrganizationUserRequest,
	CreateOrganizationUserRequest,
	UpdateOrganizationUserRequest,
} from "../types";

// ─── Query Keys ──────────────────────────────────────────────────────
export const orgUserKeys = {
	all: ["organization-users"] as const,
	lists: () => [...orgUserKeys.all, "list"] as const,
	list: (orgId: string, params?: object) => [...orgUserKeys.lists(), orgId, params] as const,
	details: () => [...orgUserKeys.all, "detail"] as const,
	detail: (orgId: string, id: string) => [...orgUserKeys.details(), orgId, id] as const,
};

// ─── useOrganizationUsers (list) ─────────────────────────────────────
export function useOrganizationUsers(
	organizationId: string,
	params?: { page?: number; limit?: number }
) {
	return useQuery({
		queryKey: orgUserKeys.list(organizationId, params),
		queryFn: () => organizationUsersService.list(organizationId, params),
		enabled: !!organizationId,
	});
}

// ─── useOrganizationUser (getById) ───────────────────────────────────
export function useOrganizationUser(organizationId: string, id: string) {
	return useQuery({
		queryKey: orgUserKeys.detail(organizationId, id),
		queryFn: () => organizationUsersService.getById(organizationId, id),
		enabled: !!organizationId && !!id,
	});
}

// ─── useCreateOrganizationUser ────────────────────────────────────────
export function useCreateOrganizationUser(organizationId: string) {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (
			data:
				| CreateOrganizationUserRequest
				| CreateNewOrganizationUserRequest
				| { userIds: string[]; positionId: string; isPrimary?: boolean }
		) => {
			if ("userIds" in data) {
				const { userIds, positionId, isPrimary } = data;
				const body = userIds.map((userId, index) => ({
					userId,
					positionId,
					isPrimary: index === 0 ? (isPrimary ?? false) : false,
				}));
				return organizationUsersService.create(organizationId, body);
			}
			if ("fullname" in data && "phone" in data && "password" in data) {
				return organizationUsersService.create(organizationId, [
					{
						fullname: data.fullname,
						phone: data.phone,
						password: data.password,
						positionId: data.positionId,
						isPrimary: data.isPrimary,
					},
				]);
			}
			return organizationUsersService.create(organizationId, data);
		},
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: orgUserKeys.lists() });
			const count = "userIds" in variables ? variables.userIds.length : 1;
			message.success(
				count > 1 ? `${count} ta xodim muvaffaqiyatli qo'shildi` : "Xodim muvaffaqiyatli qo'shildi"
			);
		},
	});
}

// ─── useUpdateOrganizationUser ────────────────────────────────────────
export function useUpdateOrganizationUser(organizationId: string) {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: UpdateOrganizationUserRequest }) =>
			organizationUsersService.update(organizationId, id, data),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: orgUserKeys.lists() });
			queryClient.invalidateQueries({
				queryKey: orgUserKeys.detail(organizationId, variables.id),
			});
			message.success("Xodim muvaffaqiyatli yangilandi");
		},
	});
}

// ─── useDeleteOrganizationUser ────────────────────────────────────────
export function useDeleteOrganizationUser(organizationId: string) {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => organizationUsersService.delete(organizationId, id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: orgUserKeys.lists() });
			message.success("Xodim muvaffaqiyatli o'chirildi");
		},
	});
}
